/**
 * Loads supported portal font family names from RMS when requested, and builds react-select options
 * including any saved families not present in the catalog response.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { IOption } from '../../../components/inputs/select-dropdown/select-dropdown';
import CustomizationsService from '../../../services/api/customizations-service';
import { rootContainer, TYPE } from '../../../services/container/root';

function mergeCatalogWithExtraFamilies(
    catalogFamilyNames: string[],
    extraFontNames: Array<string | undefined>,
): IOption[] {
    const map = new Map<string, IOption>();
    for (const name of catalogFamilyNames) {
        const family = name?.trim();
        if (family) {
            map.set(family, { value: family, label: family });
        }
    }
    for (const extra of extraFontNames) {
        const trimmed = extra?.trim();
        if (trimmed && !map.has(trimmed)) {
            map.set(trimmed, { value: trimmed, label: trimmed });
        }
    }
    return Array.from(map.values()).sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }),
    );
}

export function usePortalSupportedFontOptions(
    shouldLoad: boolean,
    extraFontNames: Array<string | undefined>,
): { options: IOption[]; isLoading: boolean } {
    const [catalogFamilyNames, setCatalogFamilyNames] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const loadedRef = useRef(false);
    const delimiter = '\u0001';

    // Join the extra font names with a delimiter to avoid relying on an array reference
    const extrasKey = extraFontNames
        .filter((f): f is string => !!f?.trim())
        .map((f) => f.trim())
        .sort()
        .join(delimiter);

    useEffect(() => {
        if (!shouldLoad || loadedRef.current) {
            return;
        }
        loadedRef.current = true;
        let cancelled = false;
        setIsLoading(true);
        const service = rootContainer.get<CustomizationsService>(TYPE.CustomizationsService);
        service
            .getPortalSupportedFontFamilyNames()
            .then((payload) => {
                if (!cancelled) {
                    setCatalogFamilyNames(Array.isArray(payload) ? payload : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    loadedRef.current = false;
                    setCatalogFamilyNames([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [shouldLoad]);

    const options = useMemo(() => {
        const extrasParsed = extrasKey === '' ? [] : extrasKey.split(delimiter);
        return mergeCatalogWithExtraFamilies(catalogFamilyNames, extrasParsed);
    }, [catalogFamilyNames, extrasKey]);

    return { options, isLoading };
}
